import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import ReactFlow, {
  Background,
  Controls,
  ReactFlowProvider,
  useReactFlow,
  Node,
  Connection
} from 'reactflow';
import { Typography } from '@uniquegood/realworld-studio-design';
import { useParams } from 'react-router-dom';
import 'reactflow/dist/style.css';

import { useRequest } from '@/hooks';
import { FlexContentCenter } from '@/styles';
import { onMobile } from '@/styles/responsive';

import { nodeTypes } from './nodes/nodeTypes';
import { edgeTypes } from './edges/edgeTypes';
import useFlowData from './hooks/useFlowData';
import useFlowLayout from './hooks/useFlowLayout';
import useNodePositions from './hooks/useNodePositions';
import useFlowActions from './hooks/useFlowActions';
import useQuestDependencies from './hooks/useQuestDependencies';
import useQuestMissionTransitions from './hooks/useQuestMissionTransitions';
import useMissionDependencies from './hooks/useMissionDependencies';
import FlowToolbar from './components/FlowToolbar';
import FlowSidePanel from './components/FlowSidePanel';
import FlowContextMenu from './components/FlowContextMenu';
import { FlowContainerStyle } from './styles';
import type { SidePanelMode } from './types';

interface MissionFlowViewProps {
  onSwitchToCardMode: () => void;
}

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  missionId: number | null;
}

function parseNodeId(nodeId: string, prefix: 'quest' | 'mission'): number | null {
  const [nodePrefix, rawId] = nodeId.split('-');
  if (nodePrefix !== prefix) return null;
  const parsed = Number(rawId);
  return Number.isFinite(parsed) ? parsed : null;
}

function MissionFlowCanvas({ onSwitchToCardMode }: MissionFlowViewProps) {
  const { appId = '', projectId = '', scenarioId = '' } = useParams<AppParam>();
  const { data: missions, mutate } = useRequest<Mission[]>(
    `/apps/${appId}/scenarios/${scenarioId}/missions`
  );

  const [sidePanelMode, setSidePanelMode] = useState<SidePanelMode>({ type: 'none' });
  const [showAllDependencies, setShowAllDependencies] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    missionId: null
  });

  const prevMissionCountRef = useRef<number>(0);
  const prevQuestCountRef = useRef<number>(0);
  const hasInitialLayoutRef = useRef(false);
  const reactFlowInstance = useReactFlow();

  const { getLayoutedElements } = useFlowLayout();
  const { savePositions, loadPositions, clearPositions } = useNodePositions(appId, scenarioId);
  const { addMission, deleteMission, addQuest, connectQuestToMission, connectQuestToQuest } =
    useFlowActions(appId, scenarioId, mutate);
  const questDependencies = useQuestDependencies(missions);
  const questMissionTransitions = useQuestMissionTransitions(missions);
  const missionDependencies = useMissionDependencies(missions);

  const onSelectMission = useCallback((missionId: number) => {
    setSidePanelMode({ type: 'mission', missionId });
  }, []);

  const onSelectQuest = useCallback((missionId: number, questId: number) => {
    setSidePanelMode({ type: 'quest', missionId, questId });
  }, []);

  const onAddQuest = useCallback(
    async (missionId: number) => {
      const quest = await addQuest(missionId);
      if (quest) {
        setSidePanelMode({ type: 'quest', missionId, questId: quest.id });
      }
    },
    [addQuest]
  );

  const onToggleExpand = useCallback((missionIdToToggle: number) => {
    String(missionIdToToggle);
  }, []);

  const callbacks = useMemo(
    () => ({ onSelectMission, onSelectQuest, onAddQuest, onToggleExpand }),
    [onSelectMission, onSelectQuest, onAddQuest, onToggleExpand]
  );

  const { nodes, edges, setNodes, setEdges, onNodesChange, onEdgesChange } = useFlowData(
    missions,
    sidePanelMode,
    callbacks,
    questDependencies,
    questMissionTransitions,
    missionDependencies,
    showAllDependencies
  );

  // Initial layout and position restoration
  useEffect(() => {
    if (!missions || missions.length === 0 || nodes.length === 0 || hasInitialLayoutRef.current)
      return;

    const savedPositions = loadPositions();
    if (savedPositions) {
      const topLevelNodes = nodes.filter((n) => !n.parentNode);
      const hasAllPositions = topLevelNodes.every((n) => savedPositions[n.id]);
      if (hasAllPositions) {
        const repositionedNodes = nodes.map((node) =>
          !node.parentNode && savedPositions[node.id]
            ? { ...node, position: savedPositions[node.id] }
            : node
        );
        setNodes(repositionedNodes);
        hasInitialLayoutRef.current = true;
        setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 50);
        return;
      }
    }

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    hasInitialLayoutRef.current = true;
    setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 50);
  }, [missions, nodes.length]);

  // Re-layout when mission count changes
  useEffect(() => {
    if (!missions) return;
    if (prevMissionCountRef.current > 0 && missions.length !== prevMissionCountRef.current) {
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      clearPositions();
      setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 50);
    }
    prevMissionCountRef.current = missions.length;
  }, [missions?.length]);

  // Re-layout when quest count changes
  useEffect(() => {
    if (!missions) return;
    const questCount = missions.reduce((acc, mission) => acc + (mission.quests?.length ?? 0), 0);

    if (prevQuestCountRef.current > 0 && questCount !== prevQuestCountRef.current) {
      requestAnimationFrame(() => {
        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
        clearPositions();
        setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 50);
      });
    }

    prevQuestCountRef.current = questCount;
  }, [missions, nodes, edges]);

  const handleAutoLayout = useCallback(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    clearPositions();
    setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 50);
  }, [nodes, edges, getLayoutedElements, setNodes, setEdges, clearPositions, reactFlowInstance]);

  const handleAddMission = useCallback(async () => {
    await addMission(missions?.length);
  }, [addMission, missions?.length]);

  const handleDeleteMission = useCallback(
    async (missionId: number) => {
      await deleteMission(missionId);
      setSidePanelMode({ type: 'none' });
    },
    [deleteMission]
  );

  const handleNodeDragStop = useCallback(
    (_: unknown, __: unknown, allNodes: Node[]) => {
      savePositions(allNodes);
    },
    [savePositions]
  );

  const handleNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (node.type !== 'missionNode') return;
      event.preventDefault();
      const missionId = node.data?.mission?.id;
      if (!missionId) return;
      setContextMenu({ visible: true, x: event.clientX, y: event.clientY, missionId });
    },
    []
  );

  const handlePaneClick = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleConnect = useCallback(
    async ({ source, target }: Connection) => {
      if (!source || !target || !missions) return;

      const sourceQuestId = parseNodeId(source, 'quest');
      if (!sourceQuestId) return;

      const targetMissionId = parseNodeId(target, 'mission');
      if (targetMissionId) {
        await connectQuestToMission(sourceQuestId, targetMissionId, missions);
        return;
      }

      const targetQuestId = parseNodeId(target, 'quest');
      if (targetQuestId) {
        await connectQuestToQuest(sourceQuestId, targetQuestId, missions);
      }
    },
    [missions, connectQuestToMission, connectQuestToQuest]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSidePanelMode({ type: 'none' });
        setContextMenu((prev) => ({ ...prev, visible: false }));
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  if (!missions) {
    return (
      <div css={[FlowContainerStyle, FlexContentCenter]}>
        <Typography as="p" type="body" textColor="subdued">
          플로우를 불러오는 중이에요
        </Typography>
      </div>
    );
  }

  return (
    <div css={FlowContainerStyle}>
      <FlowToolbar
        onAddMission={handleAddMission}
        onAutoLayout={handleAutoLayout}
        showAllDependencies={showAllDependencies}
        onToggleDependencyVisibility={() =>
          setShowAllDependencies((prevVisibility) => !prevVisibility)
        }
        onSwitchToCardMode={onSwitchToCardMode}
      />

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={handleConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeDragStop={handleNodeDragStop}
        onNodeContextMenu={handleNodeContextMenu}
        onPaneClick={handlePaneClick}
        fitView
        minZoom={0.2}
        maxZoom={2}
        defaultEdgeOptions={{ type: 'flowEdge' }}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={24} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>

      <FlowSidePanel
        mode={sidePanelMode}
        onClose={() => setSidePanelMode({ type: 'none' })}
        mutate={mutate}
        onDeleteMission={handleDeleteMission}
      />

      {contextMenu.visible && contextMenu.missionId && (
        <FlowContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onAddQuest={() => {
            onAddQuest(contextMenu.missionId!);
            setContextMenu((prev) => ({ ...prev, visible: false }));
          }}
          onDeleteMission={() => {
            handleDeleteMission(contextMenu.missionId!);
            setContextMenu((prev) => ({ ...prev, visible: false }));
          }}
          onClose={() => setContextMenu((prev) => ({ ...prev, visible: false }))}
        />
      )}

      <MobileFallback />
    </div>
  );
}

function MobileFallback() {
  return (
    <div css={MobileFallbackStyle}>
      <Typography as="p" type="body" textColor="subdued">
        플로우 뷰는 데스크톱에서 사용해 주세요
      </Typography>
    </div>
  );
}

export default function MissionFlowView(props: MissionFlowViewProps) {
  return (
    <ReactFlowProvider>
      <MissionFlowCanvas {...props} />
    </ReactFlowProvider>
  );
}

const MobileFallbackStyle = css`
  display: none;

  ${onMobile} {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.9);
    z-index: 100;
  }
`;
